import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema } from './helpers';
import { knownTotalQuerySchema } from './known-total';
import { organizationIdSchema } from './organizations';
import { severitySchema } from './shared/severity';
import { ColumnDataTypes, createTableQuerySchema, DATE_RANGE_OPERATORS, FiltersSchema, } from './table';
import { filterOptionItemSchema } from './mcp';
import { loggedInUserCountsSchema, loggedInUserMatchSchema, } from './shadow-ai-logged-in-user';
export const shadowAiCategorySchema = z.enum([
    'chatbot',
    'code_assist',
    'llm_api',
    'llm_local',
]);
export const categoryLabels = {
    chatbot: 'Chatbot',
    code_assist: 'Code Assistant',
    llm_api: 'LLM API',
    llm_local: 'Local LLM',
};
/**
 * Human-readable labels for every Shadow AI category key stored or shown in the product.
 * Keys are snake_case (e.g. code_assist). Used for table cells, filters, and CSV-adjacent UI.
 */
export const shadowAiCategoryLabels = {
    ...categoryLabels,
    agent: 'Agent',
    assistant: 'Assistant',
    ai_browser: 'AI Browser',
    automation: 'Automation',
    browser_extension: 'Browser Extension',
    code_repository: 'Code Repository',
    design_ai: 'Design (AI features)',
    ide: 'IDE',
    image_gen: 'Image Gen',
    llm: 'LLM',
    audio_gen: 'Audio Gen',
    video_gen: 'Video Gen',
    agent_dev_platform: 'Agent Dev Platform',
    search_engine: 'Search Engine',
    tool: 'Tool',
    other: 'Other',
};
function formatUnknownShadowAiCategoryLabel(category) {
    const trimmed = category.trim();
    if (trimmed === '') {
        return category;
    }
    return trimmed
        .split('_')
        .map(segment => {
        const lower = segment.toLowerCase();
        if (lower === 'llm')
            return 'LLM';
        if (lower === 'api')
            return 'API';
        if (lower === 'ai')
            return 'AI';
        if (lower === 'id')
            return 'ID';
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
        .join(' ');
}
/** Resolves a stored category key to the same label used in filters and tables. */
export function getShadowAiCategoryLabel(category) {
    return (shadowAiCategoryLabels[category] ??
        formatUnknownShadowAiCategoryLabel(category));
}
/**
 * Kind of local artifact the agent read the logged-in account from. Metadata-only
 * (account hints) — never decrypted secrets or credential values (KIR-2916).
 */
export const loggedInUserSourceSchema = z.enum([
    'config_file',
    'token_file',
    'cookie',
    'localstorage',
    'indexeddb',
    'keychain_metadata',
]);
/**
 * Why a Shadow AI detection has no LoggedInUser. Emitted by the agent in place of
 * a {@link loggedInUserSchema} value.
 */
export const loggedInUserReasonSchema = z.enum([
    'signed_out',
    'unsupported',
    'no_local_artifact',
]);
/**
 * Normalised logged-in account extracted from a Shadow AI tool's local artifacts.
 * `accountEmail` is the primary identity; `accountId` is the fallback stable id when
 * the tool exposes no email.
 */
export const loggedInUserSchema = z.object({
    accountEmail: z.string().nullable(),
    accountId: z.string().nullable(),
    tenant: z.string().nullable(),
    displayName: z.string().nullable(),
    provider: z.string(),
    source: loggedInUserSourceSchema,
});
// Re-exported (imported above) so existing `contracts/shadow-ai` consumers keep
// working; the canonical definitions live in the leaf module to avoid an import
// cycle with the list-item / posture contracts (KIR-2916).
export { loggedInUserCountsSchema, loggedInUserMatchSchema, };
/**
 * Normalises an email for comparison: trims, lowercases, and strips any `+alias`
 * suffix from the local part. Malformed input (no `@`) is compared verbatim.
 */
function normalizeEmailForMatch(email) {
    const trimmed = email.trim().toLowerCase();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0 || at === trimmed.length - 1) {
        return trimmed;
    }
    const local = trimmed.slice(0, at);
    const domain = trimmed.slice(at + 1);
    const plus = local.indexOf('+');
    const normalizedLocal = plus === -1 ? local : local.slice(0, plus);
    return `${normalizedLocal}@${domain}`;
}
/** Returns the lowercased domain of an email, or null when it has no domain part. */
function emailDomain(email) {
    const trimmed = email.trim().toLowerCase();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0 || at === trimmed.length - 1) {
        return null;
    }
    return trimmed.slice(at + 1);
}
/**
 * Classifies a Shadow AI detection's logged-in account against the Kirin user it is
 * attributed to. See {@link loggedInUserMatchSchema} for the semantics of each value.
 * Matching is case-insensitive and tolerant of `+aliases`.
 */
export function classifyLoggedInUserMatch(loggedInUserEmail, kirinUserEmail) {
    if (!loggedInUserEmail || loggedInUserEmail.trim() === '') {
        return 'unknown';
    }
    if (!kirinUserEmail || kirinUserEmail.trim() === '') {
        return 'personal';
    }
    if (normalizeEmailForMatch(loggedInUserEmail) ===
        normalizeEmailForMatch(kirinUserEmail)) {
        return 'matched';
    }
    const loggedInDomain = emailDomain(loggedInUserEmail);
    const kirinDomain = emailDomain(kirinUserEmail);
    if (loggedInDomain && kirinDomain && loggedInDomain === kirinDomain) {
        return 'corporate';
    }
    return 'personal';
}
export const shadowAiFindingSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
    sources: z.array(z.string()).optional(),
    paths: z.array(z.string()).optional(),
    version: z.string().optional(),
    lastUsed: z.string().datetime({ offset: true }).optional(),
    addedAt: z.string().datetime({ offset: true }).optional(),
    isRunning: z.boolean().optional(),
    extras: z.record(z.string(), z.string()).optional(),
    loggedInUser: loggedInUserSchema.nullish(),
    loggedInUserReason: loggedInUserReasonSchema.nullish(),
});
export const shadowAiRuntimeFindingSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
    pid: z.number().int(),
    ppid: z.number().int().optional(),
    port: z.number().int().optional(),
    user: z.string().optional(),
    binaryPath: z.string().optional(),
    commandLine: z.string().optional(),
    parentName: z.string().optional(),
    isRoot: z.boolean().optional(),
    extras: z.record(z.string(), z.string()).optional(),
    startedAt: z.string().datetime({ offset: true }),
    loggedInUser: loggedInUserSchema.nullish(),
    loggedInUserReason: loggedInUserReasonSchema.nullish(),
});
export const shadowAiSniFindingSchema = z.object({
    domain: z.string(),
    source: z.string(), // "tls", "dns", "streaming"
    pid: z.number().int().optional(),
    processName: z.string().optional(),
    ja4: z.string().optional(),
    tlsVersion: z.string().optional(),
    isProgrammatic: z.boolean().optional(),
    isStreaming: z.boolean().optional(),
    isAsymmetric: z.boolean().optional(),
    isLongLived: z.boolean().optional(),
    packetsIn: z.number().int().optional(),
    packetsOut: z.number().int().optional(),
    bytesIn: z.number().int().optional(),
    bytesOut: z.number().int().optional(),
    byteRatio: z.number().optional(),
    durationMs: z.number().int().optional(),
});
export const shadowAiReportRequestSchema = z.object({
    detected: z.array(shadowAiFindingSchema).optional(),
    running: z.array(shadowAiRuntimeFindingSchema).optional(),
    sni: z.array(shadowAiSniFindingSchema).optional(),
});
export const shadowAiToolListItemSchema = z.object({
    name: z.string(),
    toolId: z.string(),
    category: z.string(),
    sources: z.array(z.string()),
    version: z.string().nullable(),
    userCount: z.number().int().min(0),
    addedAt: z.string().datetime().nullable(),
    lastUsed: z.string().datetime().nullable(),
    extras: z.record(z.string(), z.string()).nullable(),
    riskLevel: severitySchema,
});
export const shadowAiToolListResponseSchema = z.object({
    tools: z.array(shadowAiToolListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
export const shadowAiToolUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    machineId: z.string(),
    // KIR-3035: resolved endpoint (machine) name for machineId; shown in the
    // renamed "Endpoint" column. Optional/nullable when no endpoint is registered.
    endpointName: z.string().nullable().optional(),
    timestamp: z.string().datetime(),
    /** Logged-in account extracted for this tool on this user's machine, if any. */
    loggedInUser: loggedInUserSchema.nullish(),
    /** Backend-computed relationship to {@link email}; defaults to `unknown`. */
    loggedInUserMatch: loggedInUserMatchSchema.default('unknown'),
    /** Reason a LoggedInUser is absent, when {@link loggedInUser} is null. */
    loggedInUserReason: loggedInUserReasonSchema.nullish(),
});
export const shadowAiToolUsersResponseSchema = z.object({
    toolId: z.string(),
    users: z.array(shadowAiToolUserSchema),
});
export const shadowAiTopUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    installedCount: z.number().int().min(0),
    browserCount: z.number().int().min(0),
    addedAt: z.string().datetime().nullable(),
    extras: z.record(z.string(), z.string()).nullable(),
});
export const shadowAiTopUsersResponseSchema = z.object({
    users: z.array(shadowAiTopUserSchema),
});
export const shadowAiTopToolSchema = z.object({
    toolId: z.string(),
    name: z.string(),
    category: z.string(),
    riskLevel: severitySchema,
    userCount: z.number().int().min(0),
    sources: z.array(z.string()),
    addedAt: z.string().datetime().nullable(),
    lastUsed: z.string().datetime().nullable(),
});
export const shadowAiTopToolsResponseSchema = z.object({
    tools: z.array(shadowAiTopToolSchema),
    totalCount: z.number().int().min(0),
});
export const runningProcessListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    toolId: z.string(),
    category: z.string(),
    user: z.string(),
    userId: z.string().uuid(),
    userEmail: z.string().nullable(),
    userName: z.string().nullable(),
    pid: z.number().int(),
    ppid: z.number().int().nullable(),
    port: z.number().int().nullable(),
    binaryPath: z.string().nullable(),
    commandLine: z.string().nullable(),
    parentName: z.string().nullable(),
    isRoot: z.boolean(),
    extras: z.record(z.string(), z.string()).nullable(),
    startedAt: z.string().datetime({ offset: true }),
});
export const runningProcessListResponseSchema = z.object({
    processes: z.array(runningProcessListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    lastHeartbeat: z.string().datetime().nullable(),
});
export const shadowAiOrgStatsSchema = z.object({
    totalTools: z.number().int().min(0),
    highRiskCount: z.number().int().min(0),
    rootProcessCount: z.number().int().min(0),
    newThisWeekCount: z.number().int().min(0),
    activeProcesses: z.number().int().min(0),
    aiConnectionsCount: z.number().int().min(0),
    pendingReviewCount: z.number().int().min(0),
});
export const networkConnectionStatusSchema = z.enum([
    'approved',
    'flagged',
    'pending',
    'ignored',
]);
export const shadowAiConfidenceLabelSchema = z.enum([
    'likely_ai',
    'possible_ai',
    'unlikely_ai',
]);
export const shadowAiConfidenceTagSchema = z.enum([
    'streaming',
    'programmatic',
    'ai_pattern',
    'infra',
]);
export const shadowAiConfidenceSchema = z.object({
    score: z.number().int().min(0).max(99),
    label: shadowAiConfidenceLabelSchema,
    tags: z.array(shadowAiConfidenceTagSchema),
});
export const confidenceLabelLabels = {
    likely_ai: 'Likely AI',
    possible_ai: 'Possible AI',
    unlikely_ai: 'Unlikely AI',
};
export const networkConnectionListItemSchema = z.object({
    processName: z.string().nullable(),
    domain: z.string(),
    status: networkConnectionStatusSchema,
    ignoreSource: z.enum(['global', 'org']).nullable(),
    identificationSource: z.enum(['global', 'org']).nullable(),
    hitCount: z.number().int(),
    uniqueUsers: z.number().int(),
    uniqueMachines: z.number().int(),
    totalBytesIn: z.number().int(),
    totalBytesOut: z.number().int(),
    ja4Fingerprints: z.array(z.string()),
    isStreaming: z.boolean().nullable(),
    isProgrammatic: z.boolean().nullable(),
    isAsymmetric: z.boolean().nullable(),
    isLongLived: z.boolean().nullable(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
    classifiedBy: z.string().nullable(),
    classifiedByEmail: z.string().nullable(),
    classifiedByName: z.string().nullable(),
    classifiedAt: z.string().datetime().nullable(),
    identifiedAgentName: z.string().nullable(),
    confidence: shadowAiConfidenceSchema.optional().nullable(),
});
export const networkConnectionsResponseSchema = z.object({
    connections: z.array(networkConnectionListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(1000),
        total: z.number().int().min(0),
        totalPages: z.number().int().min(0),
    }),
});
export const networkConnectionUserSchema = z.object({
    userId: z.string(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    machineId: z.string(),
    // KIR-3035: resolved endpoint (machine) name for machineId; shown in the
    // renamed "Endpoint" column. Optional/nullable when no endpoint is registered.
    endpointName: z.string().nullable().optional(),
    hitCount: z.number().int(),
    bytesIn: z.number().int().nullable(),
    bytesOut: z.number().int().nullable(),
    ja4: z.string().nullable(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
});
export const networkConnectionUsersResponseSchema = z.object({
    users: z.array(networkConnectionUserSchema),
});
// Network Discovery / Pending Domains
export const pendingDomainClassificationSchema = z.enum([
    'ai_approved',
    'ai_flagged',
    'ignore',
]);
export const pendingDomainSchema = z.object({
    domain: z.string(),
    hitCount: z.number().int(),
    uniqueUsers: z.number().int(),
    uniqueMachines: z.number().int(),
    sampleProcess: z.string().nullable(),
    sampleJa4: z.string().nullable(),
    isStreaming: z.boolean().nullable(),
    isProgrammatic: z.boolean().nullable(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
});
export const pendingDomainsResponseSchema = z.object({
    domains: z.array(pendingDomainSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
export const pendingDomainUserSchema = z.object({
    userId: z.string(),
    userEmail: z.string().nullable(),
    userName: z.string().nullable(),
    machineId: z.string(),
    hitCount: z.number().int(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
});
export const pendingDomainDetailSchema = pendingDomainSchema.extend({
    users: z.array(pendingDomainUserSchema),
});
export const orgIgnoreDomainSchema = z.object({
    domain: z.string(),
    addedBy: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const orgIgnoreDomainsResponseSchema = z.object({
    domains: z.array(orgIgnoreDomainSchema),
});
export const globalIgnoreDomainSchema = z.object({
    domain: z.string(),
    category: z.string().nullable(),
    createdAt: z.string().datetime(),
    isPreset: z.boolean().optional(),
});
export const globalIgnoreDomainsResponseSchema = z.object({
    domains: z.array(globalIgnoreDomainSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
export const classifiedDomainStatusSchema = z.enum(['approved', 'flagged']);
export const classifiedDomainSchema = z.object({
    id: z.string(),
    domain: z.string(),
    status: classifiedDomainStatusSchema,
    category: z.string().nullable(),
    name: z.string().nullable(),
    notes: z.string().nullable(),
    classifiedBy: z.string().nullable(),
    classifiedByEmail: z.string().nullable(),
    classifiedByName: z.string().nullable(),
    classifiedAt: z.string().datetime(),
});
export const classifiedDomainsResponseSchema = z.object({
    domains: z.array(classifiedDomainSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
export const networkTrafficStatsSchema = z.object({
    totalDomains: z.number().int(),
    pendingCount: z.number().int(),
    approvedCount: z.number().int(),
    flaggedCount: z.number().int(),
    totalBytesIn: z.number().int(),
    totalBytesOut: z.number().int(),
    uniqueJa4Count: z.number().int(),
    uniqueMachines: z.number().int(),
    topJa4: z.array(z.object({
        ja4: z.string(),
        domainCount: z.number().int(),
        hitCount: z.number().int(),
    })),
});
// ============================================================================
// Query Schemas
// ============================================================================
export const shadowAiToolSortFieldSchema = z.enum([
    'name',
    'category',
    'addedAt',
    'lastUsed',
    'userCount',
]);
export const runningProcessSortFieldSchema = z.enum([
    'name',
    'category',
    'user',
    'startedAt',
]);
export const shadowAiSourceTypeSchema = z.enum(['installed', 'browser']);
export const shadowAiToolListBaseQuerySchema = baseListQuerySchema.extend({
    categories: z
        .preprocess(value => typeof value === 'string' ? value.split(',').filter(Boolean) : value, z.array(z.string()))
        .optional(),
    sourceType: shadowAiSourceTypeSchema.optional(),
    sortBy: shadowAiToolSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
});
export const shadowAiToolListQuerySchema = shadowAiToolListBaseQuerySchema.extend({
    userId: z.string().uuid().optional(),
});
export const runningProcessListBaseQuerySchema = baseListQuerySchema.extend({
    sortBy: runningProcessSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
});
export const runningProcessListQuerySchema = runningProcessListBaseQuerySchema.extend({
    userId: z.string().uuid().optional(),
    isRoot: z.coerce.boolean().optional(),
});
export const networkConnectionSortFieldSchema = z.enum([
    'hitCount',
    'lastSeen',
    'domain',
    'processName',
    'status',
    'totalBytes',
    'uniqueMachines',
    'uniqueUsers',
]);
/**
 * Indicates what kind of client-driven change triggered this request.
 * The backend uses this together with `knownTotal` to decide whether the
 * expensive COUNT query can be skipped:
 *
 * - `filters` (default) — first load, filter/search change, or mutation
 *   invalidation. Always runs COUNT.
 * - `sort` — only the sort changed. The total cannot have changed, so the
 *   backend trusts `knownTotal` and skips COUNT entirely (including the
 *   page-1 recount that `finalizeTotal` normally performs).
 * - `page` — only the page changed. Same as `sort`.
 */
export const networkConnectionsCountScopeSchema = z.enum([
    'filters',
    'sort',
    'page',
]);
// ============================================================================
// Network Connections DataTable filtering (KIR-3496)
// Per-column Filter/Sort/Search on the Shadow AI → Network Discovery table,
// mirroring the Browser AI Tools table. Field ids match the sort-field enum
// (and the `domain_enriched` columns the repository filters on).
// ============================================================================
export const NETWORK_CONNECTIONS_FILTERABLE_FIELDS = {
    PROCESS: 'processName',
    HOST: 'domain',
    STATUS: 'status',
    TRAFFIC: 'totalBytes',
    HITS: 'hitCount',
    USERS: 'uniqueUsers',
    MACHINES: 'uniqueMachines',
    LAST_SEEN: 'lastSeen',
};
export const NETWORK_CONNECTIONS_FILTER_COLUMN_DATA_TYPES = {
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.PROCESS]: ColumnDataTypes.STRING,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.HOST]: ColumnDataTypes.STRING,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.STATUS]: ColumnDataTypes.ENUM,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.TRAFFIC]: ColumnDataTypes.NUMBER,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.HITS]: ColumnDataTypes.NUMBER,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.NUMBER,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.MACHINES]: ColumnDataTypes.NUMBER,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.LAST_SEEN]: ColumnDataTypes.DATE,
};
// Last Seen drops equals/notEquals — range operators + value-less only.
export const NETWORK_CONNECTIONS_OPERATOR_OVERRIDES = {
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.LAST_SEEN]: DATE_RANGE_OPERATORS,
};
const networkConnectionsQueryBaseSchema = baseListQuerySchema.extend({
    status: z
        .enum(['all', 'pending', 'approved', 'flagged', 'ignored'])
        .optional(),
    sortBy: networkConnectionSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
    countScope: networkConnectionsCountScopeSchema.optional(),
});
export const networkConnectionsQuerySchema = createTableQuerySchema(networkConnectionsQueryBaseSchema, NETWORK_CONNECTIONS_FILTER_COLUMN_DATA_TYPES, NETWORK_CONNECTIONS_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        if (filter.field === NETWORK_CONNECTIONS_FILTERABLE_FIELDS.LAST_SEEN) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().datetime({ offset: true, local: true }).safeParse(v)
                    .success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Filter values for lastSeen field must be valid ISO 8601 datetime strings',
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
    });
});
// ----------------------------------------------------------------------------
// Network Connections filter-options (dynamic dropdowns for process/host)
// ----------------------------------------------------------------------------
export const NETWORK_CONNECTIONS_FILTER_OPTION_FIELDS = {
    PROCESS: 'processName',
    HOST: 'domain',
};
export const networkConnectionsFilterOptionFieldSchema = z.enum([
    NETWORK_CONNECTIONS_FILTER_OPTION_FIELDS.PROCESS,
    NETWORK_CONNECTIONS_FILTER_OPTION_FIELDS.HOST,
]);
export const networkConnectionsFilterOptionsQuerySchema = z.object({
    field: networkConnectionsFilterOptionFieldSchema,
    // KIR-3496: scope the options to the current status view so the dropdown
    // only offers hosts/processes present in the rows the user is looking at.
    status: z
        .enum(['all', 'pending', 'approved', 'flagged', 'ignored'])
        .optional(),
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
export const networkConnectionsFilterOptionsResponseSchema = z.object({
    field: networkConnectionsFilterOptionFieldSchema,
    options: z.union([z.array(z.string()), z.array(filterOptionItemSchema)]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
export const pendingDomainsSortFieldSchema = z.enum(['hitCount', 'lastSeen']);
export const pendingDomainsQuerySchema = baseListQuerySchema.extend({
    sortBy: pendingDomainsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
});
export const globalIgnoreDomainsQuerySchema = baseListQuerySchema;
export const classifiedDomainsQuerySchema = baseListQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: classifiedDomainStatusSchema.optional(),
});
export const sniEventsQuerySchema = baseListQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    reviewed: z.enum(['all', 'reviewed', 'unreviewed']).optional(),
});
export const shadowAiAgentsQuerySchema = baseListQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: z
        .enum(['unreviewed', 'identified', 'dismissed', 'known_tool'])
        .optional(),
    minRiskScore: z.coerce.number().int().min(0).max(100).optional(),
});
// ============================================================================
// Responses + Requests for additional endpoints
// ============================================================================
export const shadowAiSuccessResponseSchema = z.object({
    success: z.boolean(),
});
export const shadowAiDomainSchema = z.string().min(1);
export const shadowAiToolIdSchema = z.string().min(1);
export const shadowAiAgentIdSchema = z.string().uuid();
export const networkDomainsResponseSchema = z.object({
    domains: z.array(z.string()),
});
export const agentNamesResponseSchema = z.object({
    names: z.array(z.object({
        name: z.string(),
        category: z.string().nullable(),
    })),
});
export const pendingDomainClassificationRequestSchema = z.object({
    classification: pendingDomainClassificationSchema,
    organizationId: organizationIdSchema,
    category: z.string().optional(),
    name: z.string().optional(),
    notes: z.string().optional(),
});
/** Body for org-scoped classify (organizationId from path). */
export const pendingDomainClassificationBodySchema = z.object({
    classification: pendingDomainClassificationSchema,
    category: z.string().optional(),
    name: z.string().optional(),
    notes: z.string().optional(),
});
export const bulkClassifyDomainsRequestSchema = z.object({
    domains: z.array(z.string()).min(1).max(100),
    classification: pendingDomainClassificationSchema,
    organizationId: organizationIdSchema,
    category: z.string().optional(),
    notes: z.string().optional(),
});
/** Body for org-scoped bulk-classify (organizationId from path). */
export const bulkClassifyDomainsBodySchema = z.object({
    domains: z.array(z.string()).min(1).max(100),
    classification: pendingDomainClassificationSchema,
    category: z.string().optional(),
    notes: z.string().optional(),
    // KIR-3496: agent name for bulk "Identify All" so every classified domain is
    // stored with its agentName (not just dumped into notes).
    name: z.string().optional(),
});
export const bulkClassifyDomainsResponseSchema = z.object({
    success: z.boolean(),
    classified: z.number(),
});
export const orgIgnoreDomainRequestSchema = z.object({
    domain: z.string().min(1),
    notes: z.string().optional(),
});
export const trackGlobalDomainRequestSchema = z.object({
    domain: z.string().min(1),
});
export const classifiedDomainDeleteQuerySchema = z.object({
    organizationId: organizationIdSchema,
});
export const bloomFilterPatternSchema = z.object({
    pattern: z.string(),
    type: z.enum(['exact', 'suffix', 'glob']),
});
export const bloomFiltersResponseSchema = z.object({
    version: z.string(),
    checksum: z.string(),
    ai_filter: z.string(),
    ignore_filter: z.string(),
    ignore_patterns: z.array(bloomFilterPatternSchema),
    agent_names: z.record(z.string(), z.string()),
    linked_tool_ids: z.record(z.string(), z.string()),
});
export const bloomFilterChecksumResponseSchema = z.object({
    version: z.string(),
    checksum: z.string(),
});
export const shadowAiDetectionCatalogEntryTypeSchema = z.enum([
    'tool',
    'agent',
    'browser_extension',
    'domain',
    'ide_extension',
    'local_llm',
    'framework',
    'search_engine',
    'ai_browser',
    'desktop_app',
    'cli_tool',
]);
export const shadowAiDetectionCatalogSignaturesSchema = z
    .object({
    aliases: z.array(z.string()).optional(),
    processNames: z.array(z.string()).optional(),
    binaryNames: z.array(z.string()).optional(),
    configPaths: z.array(z.string()).optional(),
    domains: z.array(z.string()).optional(),
    browserExtensionPatterns: z.array(z.string()).optional(),
    ideExtensionIds: z.array(z.string()).optional(),
    // Per-OS overrides merged into the shared maps by the Go loader for the
    // current `runtime.GOOS` only (catalog/catalog.go:LoadDetectionCatalog).
    // Same matching semantics as the shared fields:
    //   - processNames*  : lowercased exact lookup (`.exe`/`.cmd`/`.bat`/`.ps1`
    //     stripped before lookup).
    //   - binaryNames*   : lowercased basename match (`\` → `/` normalized,
    //     same suffix stripping).
    //   - configPaths*   : case-insensitive substring against the lowercased,
    //     `\` → `/` normalized candidate path. Use lowercase Unix-shaped
    //     fragments (e.g. `appdata/roaming/cursor`) — never absolute Windows
    //     paths.
    // Use these instead of polluting the shared arrays when a path/binary
    // genuinely only exists on one OS. Entry-level `platforms` still applies
    // to the whole entry; these per-OS arrays only kick in when the entry
    // already passes `entryRunsOnCurrentPlatform`.
    processNamesWindows: z.array(z.string()).optional(),
    binaryNamesWindows: z.array(z.string()).optional(),
    configPathsWindows: z.array(z.string()).optional(),
    processNamesLinux: z.array(z.string()).optional(),
    binaryNamesLinux: z.array(z.string()).optional(),
    configPathsLinux: z.array(z.string()).optional(),
})
    .strict();
export const shadowAiDetectionCatalogEntrySchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    category: z.string().min(1),
    type: shadowAiDetectionCatalogEntryTypeSchema,
    vendor: z.string().optional(),
    platforms: z.array(z.string()).optional(),
    notes: z.string().optional(),
    linkedToolId: z.string().optional(),
    signatures: shadowAiDetectionCatalogSignaturesSchema.optional(),
})
    .strict();
export const shadowAiDetectionCatalogResponseSchema = z.object({
    version: z.string(),
    checksum: z.string(),
    entries: z.array(shadowAiDetectionCatalogEntrySchema),
});
export const shadowAiDetectionCatalogChecksumResponseSchema = z.object({
    version: z.string(),
    checksum: z.string(),
});
export const sniEventSchema = z.object({
    id: z.string(),
    domain: z.string(),
    source: z.string(),
    hitCount: z.number(),
    userCount: z.number(),
    isProgrammatic: z.boolean().nullable(),
    isStreaming: z.boolean().nullable(),
    isAsymmetric: z.boolean().nullable(),
    firstSeen: z.string(),
    lastSeen: z.string(),
    reviewed: z.boolean(),
});
export const sniEventsResponseSchema = z.object({
    events: z.array(sniEventSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        hasMore: z.boolean(),
    }),
});
export const shadowAiDomainClassificationSchema = z.enum([
    'ai_agent',
    'not_ai',
]);
export const shadowAiClassifyDomainRequestSchema = z.object({
    organizationId: organizationIdSchema,
    domain: z.string().min(1),
    classification: shadowAiDomainClassificationSchema,
    category: z.string().optional(),
    name: z.string().optional(),
    notes: z.string().optional(),
});
export const shadowAiAgentStatusSchema = z.enum([
    'unreviewed',
    'identified',
    'dismissed',
    'known_tool',
]);
export const shadowAiAgentSchema = z.object({
    id: z.string(),
    userId: z.string(),
    userEmail: z.string().nullable(),
    userName: z.string().nullable(),
    machineId: z.string(),
    processName: z.string().nullable(),
    processPath: z.string().nullable(),
    ppid: z.number().nullable(),
    parentProcessName: z.string().nullable(),
    ja4Fingerprint: z.string().nullable(),
    status: shadowAiAgentStatusSchema,
    riskScore: z.number(),
    linkedToolId: z.string().nullable(),
    firstSeen: z.string(),
    lastSeen: z.string(),
    totalCalls: z.number(),
    totalBytesIn: z.number(),
    totalBytesOut: z.number(),
    llmDomains: z.array(z.string()),
});
export const shadowAiAgentCallSchema = z.object({
    domain: z.string(),
    hitCount: z.number(),
    bytesIn: z.number(),
    bytesOut: z.number(),
    isStreaming: z.boolean().nullable(),
    isProgrammatic: z.boolean().nullable(),
    isAsymmetric: z.boolean().nullable(),
    isLongLived: z.boolean().nullable(),
    tlsVersion: z.string().nullable(),
    firstSeen: z.string(),
    lastSeen: z.string(),
});
export const shadowAiAgentsResponseSchema = z.object({
    agents: z.array(shadowAiAgentSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        hasMore: z.boolean(),
    }),
});
export const shadowAiAgentDetailSchema = shadowAiAgentSchema.extend({
    reviewedBy: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    notes: z.string().nullable(),
    calls: z.array(shadowAiAgentCallSchema),
});
export const shadowAiAgentStatusUpdateSchema = z.object({
    status: shadowAiAgentStatusSchema,
    notes: z.string().optional(),
    linkedToolId: z.string().optional(),
});
//# sourceMappingURL=shadow-ai.js.map